import { useCallback, useEffect, useMemo, useState } from 'react'
import Card from '@/components/ui/Card'
import { useAuthContext } from '@/lib/auth-context'
import { supabase } from '@/supabase/client'
import RosterManager, { type LockerRoomMember, type ProspectBudgetPlan, type ProspectActivity } from '@/components/locker/RosterManager'

const CORE_API = import.meta.env.VITE_CORE_API_URL || ''

type LockerRoom = {
  id: string
  coach_user_id?: string | null
  name?: string | null
  public_name?: string | null
}

type Prospect = {
  id: number
  name?: string | null
  avatar_url?: string | null
  headline?: string | null
  status?: string | null
}

type RosterGroup = {
  id: string
  name: string
  description: string | null
  color: string | null
  member_count: number
}

export default function LockerRoomRoster() {
  const { profile, user } = useAuthContext() as any
  const isCoach = profile?.role === 'coach' || profile?.role === 'admin' || profile?.role === 'superadmin'

  const [authToken, setAuthToken] = useState('')
  const [lockerRoom, setLockerRoom] = useState<LockerRoom | null>(null)
  const [lockerRoomLoading, setLockerRoomLoading] = useState(false)
  const [lockerRoomError, setLockerRoomError] = useState('')

  const [roster, setRoster] = useState<LockerRoomMember[]>([])
  const [rosterLoading, setRosterLoading] = useState(false)
  const [rosterError, setRosterError] = useState('')
  const [rosterMessage, setRosterMessage] = useState('')

  const [availableProspects, setAvailableProspects] = useState<Prospect[]>([])
  const [budgetPlans, setBudgetPlans] = useState<Record<number, ProspectBudgetPlan>>({})
  const [activityByProspect] = useState<Record<number, ProspectActivity>>({})

  const [rosterGroups, setRosterGroups] = useState<RosterGroup[]>([])
  const [groupMemberships, setGroupMemberships] = useState<Record<number, string[]>>({})

  // ─── Auth token ─────────────────────────────────────────────────────────

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => { setAuthToken(data?.session?.access_token || '') })
  }, [])

  const authHeaders = useMemo(
    () => ({ Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' }),
    [authToken],
  )

  // ─── Loaders ────────────────────────────────────────────────────────────

  const loadLockerRoom = useCallback(async () => {
    if (!isCoach || !user?.id) return
    setLockerRoomLoading(true)
    setLockerRoomError('')
    const { data, error } = await supabase
      .from('locker_rooms')
      .select('id, coach_user_id, name, public_name')
      .eq('coach_user_id', user.id)
      .is('archived_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    if (error) setLockerRoomError(error.message || 'Unable to load locker room')
    else setLockerRoom(data as LockerRoom | null)
    setLockerRoomLoading(false)
  }, [isCoach, user?.id])

  const loadAvailableProspects = useCallback(async () => {
    if (!isCoach || !user?.id) return
    const { data } = await supabase
      .from('prospects')
      .select('id, name, avatar_url, headline, status')
      .eq('coach_user_id', user.id)
      .order('created_at', { ascending: false })
    setAvailableProspects((data || []) as Prospect[])
  }, [isCoach, user?.id])

  const loadRoster = useCallback(async () => {
    if (!lockerRoom?.id || !authToken) return
    setRosterLoading(true)
    setRosterError('')
    try {
      const res = await fetch(`${CORE_API}/api/v1/roster/locker/${lockerRoom.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err?.error?.message || 'Unable to load roster')
      }
      const json = await res.json()
      setRoster((json.data?.roster || []) as LockerRoomMember[])
    } catch (err: any) {
      setRosterError(err?.message || 'Unable to load roster')
    } finally {
      setRosterLoading(false)
    }
  }, [lockerRoom?.id, authToken])

  const loadBudgetPlans = useCallback(async () => {
    if (!roster.length) { setBudgetPlans({}); return }
    const ids = roster.map((m) => m.prospect_id)
    const { data } = await supabase
      .from('prospect_budgets')
      .select('prospect_id, remaining_cents, annual_limit_cents, monthly_limit_cents, soft_monthly_limit_cents, planned_annual_allocation_cents')
      .in('prospect_id', ids)
    const map: Record<number, ProspectBudgetPlan> = {}
    for (const row of data || []) map[(row as any).prospect_id] = row as ProspectBudgetPlan
    setBudgetPlans(map)
  }, [roster])

  const loadGroups = useCallback(async () => {
    if (!isCoach || !authToken) return
    try {
      const res = await fetch(`${CORE_API}/api/v1/roster/groups`, { headers: authHeaders })
      if (!res.ok) return
      const json = await res.json()
      setRosterGroups(json.data?.groups || [])
    } catch { /* ignore */ }
  }, [isCoach, authToken, authHeaders])

  const loadGroupMemberships = useCallback(async () => {
    if (!rosterGroups.length) { setGroupMemberships({}); return }
    const map: Record<number, string[]> = {}
    for (const g of rosterGroups) {
      try {
        const res = await fetch(`${CORE_API}/api/v1/roster/groups/${g.id}/members`, { headers: authHeaders })
        if (!res.ok) continue
        const json = await res.json()
        for (const m of json.data?.members || []) {
          if (!map[m.prospect_id]) map[m.prospect_id] = []
          map[m.prospect_id].push(g.id)
        }
      } catch { /* ignore */ }
    }
    setGroupMemberships(map)
  }, [rosterGroups, authHeaders])

  useEffect(() => { loadLockerRoom(); loadAvailableProspects() }, [loadLockerRoom, loadAvailableProspects])
  useEffect(() => { loadRoster() }, [loadRoster])
  useEffect(() => { loadBudgetPlans() }, [loadBudgetPlans])
  useEffect(() => { loadGroups() }, [loadGroups])
  useEffect(() => { loadGroupMemberships() }, [loadGroupMemberships])

  const linkableProspects = useMemo(() => {
    const linked = new Set(roster.map((m) => m.prospect_id))
    return availableProspects.filter((p) => !linked.has(p.id))
  }, [availableProspects, roster])

  // ─── Callbacks ──────────────────────────────────────────────────────────

  const toggleMemberActive = useCallback(async (member: LockerRoomMember, nextActive: boolean) => {
    if (!lockerRoom?.id) return
    await (supabase as any)
      .from('locker_room_members')
      .update({ active: nextActive })
      .eq('locker_room_id', lockerRoom.id)
      .eq('prospect_id', member.prospect_id)
      .eq('role', member.role || 'athlete')
    setRosterMessage(nextActive ? 'Athlete activated.' : 'Athlete deactivated.')
    await loadRoster()
  }, [lockerRoom?.id, loadRoster])

  const removeMember = useCallback(async (member: LockerRoomMember) => {
    if (!lockerRoom?.id) return
    await (supabase as any)
      .from('locker_room_members')
      .delete()
      .eq('locker_room_id', lockerRoom.id)
      .eq('prospect_id', member.prospect_id)
      .eq('role', member.role || 'athlete')
    setRosterMessage('Athlete removed.')
    await loadRoster()
  }, [lockerRoom?.id, loadRoster])

  const linkProspectById = useCallback(async (prospectId: number) => {
    if (!lockerRoom?.id) return
    await (supabase as any)
      .from('locker_room_members')
      .upsert(
        { locker_room_id: lockerRoom.id, prospect_id: prospectId, role: 'athlete', active: true },
        { onConflict: 'locker_room_id,prospect_id,role' },
      )
    setRosterMessage('Athlete added to roster.')
    await loadRoster()
  }, [lockerRoom?.id, loadRoster])

  const handleStatusChange = useCallback(async (prospectId: number, status: string) => {
    await fetch(`${CORE_API}/api/v1/roster/member/${prospectId}/status`, {
      method: 'PATCH', headers: authHeaders, body: JSON.stringify({ status }),
    })
    await loadRoster()
  }, [authHeaders, loadRoster])

  const handleBulkStatus = useCallback(async (prospectIds: number[], status: string) => {
    await fetch(`${CORE_API}/api/v1/roster/bulk-status`, {
      method: 'PATCH', headers: authHeaders, body: JSON.stringify({ prospect_ids: prospectIds, status }),
    })
    await loadRoster()
  }, [authHeaders, loadRoster])

  const handleGroupCreate = useCallback(async (name: string, color: string) => {
    await fetch(`${CORE_API}/api/v1/roster/groups`, {
      method: 'POST', headers: authHeaders, body: JSON.stringify({ name, color }),
    })
    await loadGroups()
  }, [authHeaders, loadGroups])

  const handleGroupUpdate = useCallback(async (groupId: string, name: string, color: string) => {
    await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}`, {
      method: 'PATCH', headers: authHeaders, body: JSON.stringify({ name, color }),
    })
    await loadGroups()
  }, [authHeaders, loadGroups])

  const handleGroupDelete = useCallback(async (groupId: string) => {
    await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}`, { method: 'DELETE', headers: authHeaders })
    await loadGroups()
    await loadGroupMemberships()
  }, [authHeaders, loadGroups, loadGroupMemberships])

  const handleGroupToggle = useCallback(async (groupId: string, prospectId: number, add: boolean) => {
    if (add) {
      await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}/members`, {
        method: 'POST', headers: authHeaders, body: JSON.stringify({ prospect_ids: [prospectId] }),
      })
    } else {
      await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}/members/${prospectId}`, {
        method: 'DELETE', headers: authHeaders,
      })
    }
    await loadGroupMemberships()
  }, [authHeaders, loadGroupMemberships])

  const handleBulkGroupAssign = useCallback(async (groupId: string, prospectIds: number[]) => {
    await fetch(`${CORE_API}/api/v1/roster/groups/${groupId}/members`, {
      method: 'POST', headers: authHeaders, body: JSON.stringify({ prospect_ids: prospectIds }),
    })
    await loadGroupMemberships()
  }, [authHeaders, loadGroupMemberships])

  const handleRefresh = useCallback(async () => {
    await loadRoster()
    await loadGroups()
    await loadGroupMemberships()
  }, [loadRoster, loadGroups, loadGroupMemberships])

  // Nominate a prospect from roster view (sends to pool/add-prospect)
  const handleNominate = useCallback(async (data: { name: string; email?: string; sport?: string }) => {
    const res = await fetch(`${CORE_API}/api/v1/nominations/pool/add-prospect`, {
      method: 'POST',
      headers: authHeaders,
      body: JSON.stringify({ prospect_name: data.name, prospect_email: data.email, sport: data.sport }),
    })
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      throw new Error(err?.error?.message || err?.error || 'Failed to nominate')
    }
    await loadRoster()
  }, [authHeaders, loadRoster])

  // Add by UUID — directly link a prospect by their numeric/uuid ID
  const handleAddByUuid = useCallback(async (idStr: string) => {
    const prospectId = Number(idStr)
    if (!Number.isFinite(prospectId) || !lockerRoom?.id) return
    await (supabase as any)
      .from('locker_room_members')
      .upsert(
        { locker_room_id: lockerRoom.id, prospect_id: prospectId, role: 'athlete', active: true },
        { onConflict: 'locker_room_id,prospect_id,role' },
      )
    await loadRoster()
  }, [lockerRoom?.id, loadRoster])

  // ─── Render ─────────────────────────────────────────────────────────────

  if (!isCoach) {
    return (
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-stone-900">Roster</h2>
        <p className="text-sm text-stone-600">Only coaches can manage the roster.</p>
      </section>
    )
  }

  if (lockerRoomLoading) {
    return <p className="text-sm text-stone-500">Loading locker room...</p>
  }

  if (lockerRoomError) {
    return <p className="text-sm text-brick">{lockerRoomError}</p>
  }

  if (!lockerRoom) {
    return (
      <section className="space-y-4">
        <h2 className="text-lg font-semibold text-stone-900">Roster</h2>
        <p className="text-sm text-stone-600">No locker room found. Set one up from the Home page first.</p>
      </section>
    )
  }

  return (
    <section className="space-y-6">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold text-stone-900">Roster Management</h2>
        <p className="text-sm text-stone-600">Manage athletes, sports, groups, and status. Click any athlete to edit their profile.</p>
      </div>

      <Card>
        {rosterError ? <p className="mb-3 text-sm text-brick">{rosterError}</p> : null}
        {rosterMessage ? <p className="mb-3 text-sm text-emerald-700">{rosterMessage}</p> : null}

        <RosterManager
          roster={roster}
          rosterLoading={rosterLoading}
          budgetPlans={budgetPlans}
          activityByProspect={activityByProspect}
          linkableProspects={linkableProspects}
          groups={rosterGroups}
          groupMemberships={groupMemberships}
          token={authToken}
          onToggleActive={toggleMemberActive}
          onStatusChange={handleStatusChange}
          onLinkProspect={linkProspectById}
          onRemoveMember={removeMember}
          onBulkStatus={handleBulkStatus}
          onGroupCreate={handleGroupCreate}
          onGroupUpdate={handleGroupUpdate}
          onGroupDelete={handleGroupDelete}
          onGroupToggle={handleGroupToggle}
          onBulkGroupAssign={handleBulkGroupAssign}
          onNominateProspect={handleNominate}
          onAddByUuid={handleAddByUuid}
          onRefresh={handleRefresh}
        />
      </Card>
    </section>
  )
}
